import { create } from "zustand";
import { adsSpyService } from "../services/adsSpy.service";
import type {
  SpyBrand,
  SpyAd,
  AdsFiltersData,
  AdsPagination,
  SpyTab,
} from "../types/adsSpy.types";

interface AdsSpyFilters {
  brand_id: string;
  media_type: string;
  sort: "rank" | "newest" | "oldest";
  status: "" | "active" | "expired";
  asset_type: string;
  visual_format: string;
  messaging_angle: string;
  hook_tactic: string;
  offer_type: string;
}

interface AdsSpyState {
  // Tab
  currentTab: SpyTab;
  setCurrentTab: (tab: SpyTab) => void;

  // Brands
  brands: SpyBrand[];
  brandsLoading: boolean;
  loadBrands: () => Promise<void>;

  // Ads
  ads: SpyAd[];
  adsLoading: boolean;
  pagination: AdsPagination;
  hasMore: boolean;
  loadAds: (reset?: boolean) => Promise<void>;
  loadMoreAds: () => Promise<void>;

  // Bookmarks
  bookmarks: SpyAd[];
  bookmarksLoading: boolean;
  loadBookmarks: () => Promise<void>;

  // Filters
  filters: AdsSpyFilters;
  setFilter: <K extends keyof AdsSpyFilters>(key: K, value: AdsSpyFilters[K]) => void;
  clearAiFilters: () => void;
  aiFiltersData: AdsFiltersData | null;
  loadAiFilters: () => Promise<void>;

  // Bookmark toggle
  toggleBookmark: (adId: number) => Promise<void>;

  // Expired ads tracking (client-side)
  expiredAdIds: Set<number>;
  markAdExpired: (adId: number) => void;
}

const DEFAULT_FILTERS: AdsSpyFilters = {
  brand_id: "",
  media_type: "",
  sort: "rank",
  status: "",
  asset_type: "",
  visual_format: "",
  messaging_angle: "",
  hook_tactic: "",
  offer_type: "",
};

const DEFAULT_PAGINATION: AdsPagination = {
  page: 1,
  limit: 20,
  total: 0,
  total_pages: 0,
};

export const useAdsSpyStore = create<AdsSpyState>((set, get) => ({
  // Tab
  currentTab: "radar",
  setCurrentTab: (tab) => set({ currentTab: tab }),

  // Brands
  brands: [],
  brandsLoading: false,
  loadBrands: async () => {
    set({ brandsLoading: true });
    try {
      const brands = await adsSpyService.getBrands();
      set({ brands: brands || [] });
    } catch (err) {
      console.error("Error loading brands:", err);
    } finally {
      set({ brandsLoading: false });
    }
  },

  // Ads
  ads: [],
  adsLoading: false,
  pagination: DEFAULT_PAGINATION,
  hasMore: true,
  loadAds: async (reset = true) => {
    const state = get();
    if (state.adsLoading) return;

    const page = reset ? 1 : state.pagination.page;

    set({ adsLoading: true });
    try {
      const { filters } = state;
      const params: Record<string, string | number> = {
        page,
        limit: 20,
        sort: filters.sort,
      };
      if (filters.brand_id) params.brand_id = filters.brand_id;
      if (filters.media_type) params.media_type = filters.media_type;
      if (filters.asset_type) params.asset_type = filters.asset_type;
      if (filters.visual_format) params.visual_format = filters.visual_format;
      if (filters.messaging_angle) params.messaging_angle = filters.messaging_angle;
      if (filters.hook_tactic) params.hook_tactic = filters.hook_tactic;
      if (filters.offer_type) params.offer_type = filters.offer_type;

      const result = await adsSpyService.getAds(params);
      set({
        ads: reset ? result.data : [...state.ads, ...result.data],
        pagination: result.pagination,
        hasMore: result.pagination.page < result.pagination.total_pages,
      });
    } catch (err) {
      console.error("Error loading ads:", err);
    } finally {
      set({ adsLoading: false });
    }
  },

  loadMoreAds: async () => {
    const state = get();
    if (!state.hasMore || state.adsLoading) return;
    set((s) => ({
      pagination: { ...s.pagination, page: s.pagination.page + 1 },
    }));
    await get().loadAds(false);
  },

  // Bookmarks
  bookmarks: [],
  bookmarksLoading: false,
  loadBookmarks: async () => {
    set({ bookmarksLoading: true });
    try {
      const result = await adsSpyService.getAds({
        bookmarked: "true",
        limit: 100,
      });
      set({ bookmarks: result.data });
    } catch (err) {
      console.error("Error loading bookmarks:", err);
    } finally {
      set({ bookmarksLoading: false });
    }
  },

  // Filters
  filters: { ...DEFAULT_FILTERS },
  setFilter: (key, value) => {
    set((s) => ({ filters: { ...s.filters, [key]: value } }));
  },
  clearAiFilters: () => {
    set((s) => ({
      filters: {
        ...s.filters,
        asset_type: "",
        visual_format: "",
        messaging_angle: "",
        hook_tactic: "",
        offer_type: "",
      },
    }));
  },
  aiFiltersData: null,
  loadAiFilters: async () => {
    try {
      const brandId = get().filters.brand_id;
      const data = await adsSpyService.getAiFilters(brandId || undefined);
      set({ aiFiltersData: data });
    } catch (err) {
      console.error("Error loading AI filters:", err);
    }
  },

  // Expired ads tracking
  expiredAdIds: new Set<number>(),
  markAdExpired: (adId) => {
    set((s) => {
      const next = new Set(s.expiredAdIds);
      next.add(adId);
      return { expiredAdIds: next };
    });
  },

  // Bookmark toggle
  toggleBookmark: async (adId) => {
    try {
      const bookmarked = await adsSpyService.toggleBookmark(adId);
      set((s) => ({
        ads: s.ads.map((ad) =>
          ad.id === adId ? { ...ad, is_bookmarked: bookmarked } : ad
        ),
        bookmarks: s.bookmarks.map((ad) =>
          ad.id === adId ? { ...ad, is_bookmarked: bookmarked } : ad
        ),
      }));
    } catch (err) {
      console.error("Error toggling bookmark:", err);
    }
  },
}));
